import { markInitialized, setProjectId, updateProjectMetadata } from "../ProjectSlice_Store";
import { fetchResultsRejected, setResults } from "../ResultsSlice_Store";
import { setLayoutMode } from "../LayoutSlice_Store";
import { getFeatureFlag } from "../hooks/useFeatureFlag";
import { addMessage, setBootstrapError, setBotTyping, unlockChatInput } from "./chatSlice";
import type { ChatDispatch } from "./chatSliceTypes";

/** Bootstrap acknowledgements from before the message-type taxonomy landed. */
function handleLegacyBootstrap(parsedData: any, dispatch: ChatDispatch): boolean {
  if (parsedData.meta?.messageType === "bootstrapAck") {
    dispatch(setProjectId(parsedData.projectId));
    dispatch(markInitialized());

    if (parsedData.meta?.name || parsedData.meta?.summary) {
      dispatch(
        updateProjectMetadata({ name: parsedData.meta.name, summary: parsedData.meta.summary })
      );
    }
    return true;
  }

  if (parsedData.meta?.messageType === "bootstrapError") {
    dispatch(setBootstrapError(parsedData.meta.error));
    dispatch(unlockChatInput());
    return true;
  }

  return false;
}

/**
 * The legacy shape bundles the answer and its evidence in one JSON body.
 * Always dispatch setResults — even when empty — so stale deals/docs from prior
 * turns are cleared, mirroring the results_payload path.
 */
function handleLegacyCombinedResponse(parsedData: any, dispatch: ChatDispatch): void {
  dispatch(
    addMessage({ sender: "chatbot", text: parsedData.response, turnId: parsedData.turnId })
  );

  if (parsedData.projectId) {
    dispatch(setProjectId(parsedData.projectId));
  }

  const results = parsedData.results || [];
  const documents = parsedData.documents || [];
  const inlineDealsFlag = getFeatureFlag("inlineDeals");
  dispatch(setResults({ results, documents, turnId: parsedData.turnId, inlineDealsFlag }));

  // The legacy path carries no facts, so with the flag ON nothing here should
  // auto-open the workspace.
  const hasResults = results.length > 0 || documents.length > 0;
  if (hasResults && !inlineDealsFlag) {
    dispatch(setLayoutMode("workspace"));
  }

  dispatch(unlockChatInput());
}

/** Fallback for legacy messages or unrecognized message types. */
export function handleLegacyActivity(activityText: string, dispatch: ChatDispatch): void {
  dispatch(setBotTyping(false));

  try {
    const parsedData = JSON.parse(activityText);
    if (handleLegacyBootstrap(parsedData, dispatch)) return;
    handleLegacyCombinedResponse(parsedData, dispatch);
  } catch {
    // Ultimate fallback: treat as plain text
    dispatch(addMessage({ sender: "chatbot", text: activityText }));
    dispatch(fetchResultsRejected("Response from bot was not in the expected format."));
    dispatch(unlockChatInput());
  }
}
